import trimEnd from 'lodash/trimEnd';
import Decimal from 'decimal.js';

import { pathByRouteName } from '../util/routes';
import { createSlug } from '../util/urlHelpers';
import { convertMoneyToNumber } from '../util/currency';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_PICKUP_FEE,
  LINE_ITEMS_TAX_PREFIX,
} from '../util/types';

const SIZE_LINE_ITEM_PREFIX = 'line-item/size_';
const GA_ITEM_NAME_MAX_LEN = 100;

const asArray = value => (Array.isArray(value) ? value : []);

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

const safeConvertMoneyToNumber = money => {
  try {
    const value = convertMoneyToNumber(money);
    return isFiniteNumber(value) ? value : null;
  } catch {
    return null;
  }
};

const moneyCurrency = money => {
  const currency = money?.currency;
  return typeof currency === 'string' && currency.trim() ? currency : null;
};

export const safeGtagEvent = (eventName, params) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', eventName, params);
};

export const absoluteListingUrl = (config, routeConfiguration, listing) => {
  if (!listing?.id || !config?.marketplaceRootURL) {
    return undefined;
  }
  const title = listing.attributes?.title || '';
  const slug = createSlug(title);
  const path = pathByRouteName('ListingPage', routeConfiguration, {
    id: listing.id.uuid,
    slug,
  });
  const root = trimEnd(config.marketplaceRootURL, '/');
  return `${root}${path}`;
};

const isCommissionLineItem = lineItem =>
  lineItem?.code === LINE_ITEM_PROVIDER_COMMISSION ||
  lineItem?.code === LINE_ITEM_CUSTOMER_COMMISSION;

/**
 * Customer-visible merchandise rows only (excludes commission, shipping, tax, reversals).
 * Aligns with OrderBreakdown / LineItemSubTotalMaybe exclusions.
 */
export const isMerchandiseLineItemForCustomer = lineItem => {
  const code = lineItem?.code;
  if (
    typeof code !== 'string' ||
    !lineItem?.includeFor?.includes('customer') ||
    lineItem.reversal
  ) {
    return false;
  }
  if (isCommissionLineItem(lineItem)) {
    return false;
  }
  if (code === LINE_ITEM_SHIPPING_FEE || code === LINE_ITEM_PICKUP_FEE) {
    return false;
  }
  if (LINE_ITEMS_TAX_PREFIX.some(prefix => code.startsWith(prefix))) {
    return false;
  }
  return true;
};

const decimalToNumber = d => {
  if (d instanceof Decimal) {
    return d.toNumber();
  }
  if (typeof d === 'number') {
    return d;
  }
  return Number(d);
};

const safeQuantityToNumber = quantity => {
  const value = decimalToNumber(quantity);
  return isFiniteNumber(value) && value > 0 ? value : null;
};

export const getMerchandiseLineItems = lineItems => {
  return asArray(lineItems).filter(isMerchandiseLineItemForCustomer);
};

const resolveListingGaItemName = listing => {
  const title = listing?.attributes?.title;
  if (typeof title === 'string' && title.trim()) {
    return title.trim();
  }
  const desc = listing?.attributes?.description;
  if (typeof desc === 'string' && desc.trim()) {
    const firstLine = desc.trim().split(/\r?\n/)[0];
    if (firstLine) {
      return firstLine.length > GA_ITEM_NAME_MAX_LEN
        ? firstLine.slice(0, GA_ITEM_NAME_MAX_LEN)
        : firstLine;
    }
  }
  const uuid = listing?.id?.uuid;
  if (uuid != null && String(uuid).length > 0) {
    return `Listing ${uuid}`;
  }
  return 'Item';
};

export const buildListingGaItem = ({
  listing,
  config,
  routeConfiguration,
  quantity = 1,
  itemVariant,
  unitPriceMoney,
}) => {
  const priceMoney = unitPriceMoney || listing?.attributes?.price;
  const itemId = listing?.id?.uuid;
  const price = safeConvertMoneyToNumber(priceMoney);
  const quantityNumber = safeQuantityToNumber(quantity);

  if (!itemId || price === null || !moneyCurrency(priceMoney) || quantityNumber === null) {
    return null;
  }

  const item = {
    item_id: String(itemId),
    item_name: resolveListingGaItemName(listing),
    price,
    quantity: quantityNumber,
    google_business_vertical: 'retail',
  };
  if (itemVariant) {
    item.item_variant = itemVariant;
  }
  const url = absoluteListingUrl(config, routeConfiguration, listing);
  if (url) {
    item.item_url = url;
  }
  return item;
};

export const sumItemsValue = items =>
  items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

export const trackViewItem = ({ listing, config, routeConfiguration }) => {
  const item = buildListingGaItem({
    listing,
    config,
    routeConfiguration,
    quantity: 1,
  });
  if (!item) {
    return;
  }
  const value = sumItemsValue([item]);
  const currency = moneyCurrency(listing?.attributes?.price);
  if (!currency) {
    return;
  }
  safeGtagEvent('view_item', {
    currency,
    value,
    items: [item],
  });
};

export const trackAddToCart = ({
  listing,
  config,
  routeConfiguration,
  orderedSizeWithQuantities,
  sizeEnumOptions,
}) => {
  const price = listing?.attributes?.price;
  const currency = moneyCurrency(price);
  const unitPrice = safeConvertMoneyToNumber(price);
  if (!listing?.id || !currency || unitPrice === null) {
    return;
  }

  const entries = Object.entries(orderedSizeWithQuantities || {}).filter(
    ([, q]) => Number(q) > 0
  );
  if (!entries.length) {
    return;
  }

  const variantLabel = entries
    .map(([size, q]) => {
      const label = sizeEnumOptions?.find(o => o.key === size)?.label || size;
      return `${label}×${q}`;
    })
    .join(', ');

  const totalQty = entries.reduce((sum, [, q]) => sum + Number(q), 0);
  const item = buildListingGaItem({
    listing,
    config,
    routeConfiguration,
    quantity: totalQty,
    itemVariant: variantLabel,
  });
  if (!item) {
    return;
  }
  const value = unitPrice * totalQty;
  safeGtagEvent('add_to_cart', {
    currency,
    value,
    items: [item],
  });
};

export const buildGaItemsFromLineItems = (lineItems, { listing, config, routeConfiguration }) => {
  const merchandise = getMerchandiseLineItems(lineItems);
  return merchandise
    .map(li => {
      const price = safeConvertMoneyToNumber(li?.unitPrice);
      const quantity = safeQuantityToNumber(li?.quantity != null ? li.quantity : new Decimal(1));
      const baseId = listing?.id?.uuid;
      const code = li?.code;
      const itemId =
        baseId && merchandise.length === 1
          ? String(baseId)
          : baseId && typeof code === 'string'
          ? `${baseId}-${code.replace('line-item/', '')}`
          : null;

      if (!itemId || price === null || !moneyCurrency(li?.unitPrice) || quantity === null) {
        return null;
      }

      const item = {
        item_id: itemId,
        item_name: resolveListingGaItemName(listing),
        price,
        quantity,
        google_business_vertical: 'retail',
      };
      if (code.startsWith(SIZE_LINE_ITEM_PREFIX)) {
        item.item_variant = code.slice(SIZE_LINE_ITEM_PREFIX.length);
      } else if (code !== 'line-item/item') {
        item.item_variant = code.replace('line-item/', '');
      }
      const url = absoluteListingUrl(config, routeConfiguration, listing);
      if (url) {
        item.item_url = url;
      }
      return item;
    })
    .filter(Boolean);
};

export const extractTaxAndShippingFromLineItems = lineItems => {
  let tax = 0;
  let shipping = 0;
  asArray(lineItems).forEach(li => {
    const code = li?.code;
    const lineTotal = safeConvertMoneyToNumber(li?.lineTotal);
    if (
      typeof code !== 'string' ||
      lineTotal === null ||
      !li?.includeFor?.includes('customer') ||
      li.reversal
    ) {
      return;
    }
    if (LINE_ITEMS_TAX_PREFIX.some(p => code.startsWith(p))) {
      tax += lineTotal;
    }
    if (code === LINE_ITEM_SHIPPING_FEE || code === LINE_ITEM_PICKUP_FEE) {
      shipping += lineTotal;
    }
  });
  return { tax, shipping };
};

export const trackBeginCheckout = ({ transaction, listing, config, routeConfiguration }) => {
  if (!transaction?.attributes?.payinTotal || !transaction.attributes.lineItems?.length) {
    return;
  }
  const payinTotal = transaction.attributes.payinTotal;
  const currency = moneyCurrency(payinTotal);
  const value = safeConvertMoneyToNumber(payinTotal);
  if (!currency || value === null) {
    return;
  }
  const items = buildGaItemsFromLineItems(transaction.attributes.lineItems, {
    listing,
    config,
    routeConfiguration,
  });
  if (!items.length) {
    return;
  }
  safeGtagEvent('begin_checkout', {
    currency,
    value,
    items,
  });
};

export const trackPurchase = ({ transaction, listing, config, routeConfiguration }) => {
  const transaction_id = transaction?.id?.uuid;
  if (!transaction?.attributes?.payinTotal || !transaction_id) {
    return;
  }
  const payinTotal = transaction.attributes.payinTotal;
  const currency = moneyCurrency(payinTotal);
  const value = safeConvertMoneyToNumber(payinTotal);
  if (!currency || value === null) {
    return;
  }
  const { tax, shipping } = extractTaxAndShippingFromLineItems(
    transaction.attributes.lineItems || []
  );
  const items = buildGaItemsFromLineItems(transaction.attributes.lineItems, {
    listing,
    config,
    routeConfiguration,
  });
  if (!items.length) {
    return;
  }
  const payload = {
    transaction_id,
    value,
    currency,
    items,
  };
  if (tax > 0) {
    payload.tax = tax;
  }
  if (shipping > 0) {
    payload.shipping = shipping;
  }
  safeGtagEvent('purchase', payload);
};
